import React, { useState, useEffect, useMemo } from 'react';
import { IconButton, Tooltip, CircularProgress, Modal, Box, Typography, Button, Grid2 } from '@mui/material';
import { Sparkles, Star, Clock, X, Coins } from 'lucide-react';
import useMultiplasPrediction from './useMultiplasPrediction';
import MultiplasModal from './MultiplasModal';
import { useUsageLimiter } from '../../../utils/usageLimiter';
import { getAuth } from 'firebase/auth';
import { starCoinsClient } from '../../../services/starCoinsClient';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { multiplasModalPtBR } from '../../mappings/multiplas-modal.pt-br';
import { trackInitiateStarCoinPurchase } from '../../../utils/pixels';

const MultiplasButton = ({ gameData, sport, variant = 'icon', size = 'default', onClickCapture, showConfirmation = false }) => {
  const [open, setOpen] = useState(false);
  const [showStarCoinsModal, setShowStarCoinsModal] = useState(false);
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [transactionError, setTransactionError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { loading, prediction, error, getBestBets, reset } = useMultiplasPrediction();

  // Get current user ID
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return multiplasModalPtBR[text] || text;
  };

  // Get usage limiter data including slot machine (aiSlotSpin) functionality
  const {
    canUse,
    remaining,
    resetTime,
    tier,
    checkSlotMachineLimit,
    incrementSlotMachineUsage,
    starCoins = {}
  } = useUsageLimiter(userId);

  // Check if user is pro for pricing
  const isPro = tier === 'pro';

  // Star Coins cost for xPicks (same as aiSlotSpin)
  const XPICKS_COST = 9; // Cost in Star Coins for one xPicks spin

  // Format reset time for display
  const relativeResetTime = useMemo(() => {
    if (!resetTime) return null;

    try {
      const now = new Date();
      const resetDate = new Date(resetTime);
      const diffMs = Math.max(0, resetDate - now);

      // If less than a minute, show "Soon"
      if (diffMs < 60000) return "Soon";

      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffHrs > 0) {
        return `${diffHrs}h ${diffMins}m`;
      } else {
        return `${diffMins}m`;
      }
    } catch (e) {
      console.error('Error formatting relative reset time:', e);
      return "soon";
    }
  }, [resetTime]);

  // Refresh usage status when modal opens
  useEffect(() => {
    if (userId && !open) {
      checkSlotMachineLimit();
    }
  }, [userId, open, checkSlotMachineLimit]);

  const handleClick = async (e) => {
    // If onClickCapture is provided, let the parent handle the click logic
    if (onClickCapture) {
      const shouldContinue = await onClickCapture(e, {
        userId,
        checkSlotMachineLimit,
        starCoins,
        XPICKS_COST,
        setShowStarCoinsModal,
        setTransactionInProgress,
        setTransactionError,
        incrementSlotMachineUsage,
        reset,
        setOpen,
        getBestBets,
        gameData,
        sport
      });

      // If parent returns false, stop here
      if (shouldContinue === false) return;
    }

    if (!open && userId) {
      // Check usage limit first
      const status = await checkSlotMachineLimit();

      if (!status.canUse) {
        // No free uses and no Star Coins
        setShowStarCoinsModal(true);
        return;
      }

      // If using Star Coins (no free uses remaining)
      if (status.useStarCoins) {
        // Check if we have enough Star Coins
        if ((starCoins.balance || 0) < XPICKS_COST) {
          setShowStarCoinsModal(true);
          return;
        }

        try {
          setTransactionInProgress(true);

          // Consume Star Coins
          const result = await starCoins.consumeStarCoins(XPICKS_COST, 'xPicks');

          if (!result.success) {
            setTransactionError('Failed to consume Star Coins');
            setTransactionInProgress(false);
            return;
          }

          // Balance will update automatically via real-time listener in StarCoinsDisplay
          // No need to manually refresh here
          setTransactionInProgress(false);
        } catch (err) {
          console.error('Error consuming Star Coins:', err);
          setTransactionError('Transaction failed');
          setTransactionInProgress(false);
          return;
        }
      } else {
        // Use free spin
        const incrementResult = await incrementSlotMachineUsage();
        if (!incrementResult.success) {
          console.error('Failed to increment usage:', incrementResult.error);
          return;
        }
      }

      // Now proceed with getting predictions
      reset(); // Clear any previous state first
      setOpen(true);
      await getBestBets(gameData, sport, userId);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Clear state immediately to prevent any lingering data
    reset();
    setTransactionError(null);
  };

  // Determine the tooltip text based on remaining uses
  const getTooltipText = () => {
    if (loading || transactionInProgress) return t("Loading...");
    if (!userId) return t("Sign in to use xPicks");
    if (remaining > 0) {
      return `${t("xPicks")} (${remaining} ${t("free remaining")})`;
    } else if (starCoins.balance >= XPICKS_COST) {
      return `${t("xPicks")} (${XPICKS_COST} ${t("coins")})`;
    } else {
      return `${t("xPicks")} (${t("Resets")} ${relativeResetTime || t('soon')})`;
    }
  };

  // Determine button display text for text variant
  const getButtonText = () => {
    if (loading || transactionInProgress) {
      return <CircularProgress size={10} sx={{ color: 'rgb(196 181 253)' }} />;
    }

    if (!userId) {
      return 'xPicks';
    }

    const iconSize = size === 'compact' ? 8 : 12;

    if (remaining > 0) {
      return (
        <span className="flex items-center gap-0.5">
          xPicks
          <span className={size === 'compact' ? 'text-[7px] font-normal' : 'text-[9px] font-normal'}>({remaining})</span>
        </span>
      );
    } else if (starCoins.balance >= XPICKS_COST) {
      return (
        <span className="flex items-center gap-0.5">
          xPicks
          <Star size={iconSize} className="inline" />
        </span>
      );
    } else {
      return (
        <span className="flex items-center gap-0.5">
          xPicks
          <Clock size={iconSize} className="inline" />
        </span>
      );
    }
  };

  const buttonContent = variant === 'icon' ? (
    <Tooltip title={getTooltipText()}>
      <IconButton
        onClick={handleClick}
        disabled={loading || transactionInProgress || !userId}
        sx={{
          color: 'primary.main',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          position: 'relative',
        }}
      >
        {loading || transactionInProgress ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Sparkles size={20} />
            {remaining > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '50%',
                  width: '12px',
                  height: '12px',
                  fontSize: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                }}
              >
                {remaining}
              </span>
            )}
          </>
        )}
      </IconButton>
    </Tooltip>
  ) : variant === 'terminal' ? (
    // Bloomberg Terminal Style for Homepage
    <button
      onClick={handleClick}
      disabled={loading || transactionInProgress || !userId}
      className={`py-1.5 px-2 border font-mono font-bold tracking-widest transition-all duration-150 uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
        showConfirmation
          ? 'bg-emerald-500/20 border-emerald-500/50 hover:bg-emerald-500/30 text-emerald-400'
          : 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50 text-emerald-400'
      }`}
    >
      <span className="text-[9px] leading-none">
        {loading || transactionInProgress ? 'LOADING...' : 'XPICKS'}
      </span>
    </button>
  ) : (
    <button
      onClick={handleClick}
      disabled={loading || transactionInProgress || !userId}
      className={`flex items-center justify-center gap-0.5 border font-medium tracking-[0.15em] transition-all uppercase disabled:opacity-50 disabled:cursor-not-allowed ${
        showConfirmation
          ? 'border-violet-400/40 bg-violet-600/30 hover:bg-violet-600/40 text-violet-300'
          : 'border-violet-500/30 text-violet-300/80 hover:text-violet-300 hover:border-violet-400/50 hover:bg-white/[0.02]'
      } ${
        size === 'compact'
          ? 'px-1.5 py-0.5 text-[8px] rounded-full'
          : 'px-3 py-1.5 text-xs rounded-md'
      }`}
    >
      {getButtonText()}
    </button>
  );

  return (
    <>
      {buttonContent}
      <MultiplasModal
        open={open}
        onClose={handleClose}
        loading={loading}
        prediction={prediction}
        error={error}
        gameData={gameData}
        sport={sport}
      />
      {/* Simple Star Coins Purchase Modal */}
      <Modal
        open={showStarCoinsModal}
        onClose={() => setShowStarCoinsModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(17, 25, 40, 0.98)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            p: 3,
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t("Get Star Coins")}
            </Typography>
            <IconButton
              onClick={() => setShowStarCoinsModal(false)}
              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
              size="small"
            >
              <X size={20} />
            </IconButton>
          </Box>

          {/* Packages Grid */}
          <Grid2 container spacing={2} sx={{ mb: 3 }}>
            {[
              { amount: 25, price: 1.99, popular: false },
              { amount: 100, price: 3.99, popular: true },
              { amount: 500, price: 7.99, popular: false },
            ].map((pkg) => (
              <Grid2 size={4} key={pkg.amount}>
                <Box
                  sx={{
                    backgroundColor: pkg.popular ? 'rgba(147, 51, 234, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                    border: pkg.popular ? '2px solid rgb(147, 51, 234)' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    p: 1.5,
                    textAlign: 'center',
                    cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    opacity: checkoutLoading ? 0.6 : 1,
                    '&:hover': !checkoutLoading && {
                      backgroundColor: pkg.popular ? 'rgba(147, 51, 234, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                      transform: 'scale(1.05)',
                    },
                  }}
                  onClick={async () => {
                    if (checkoutLoading) return;
                    try {
                      setCheckoutLoading(true);

                      // Track initiate checkout event for Facebook Pixel
                      trackInitiateStarCoinPurchase(`package_${pkg.amount}`, pkg.price);

                      await starCoinsClient.createCheckout(
                        `package_${pkg.amount}`,
                        pkg.amount,
                        pkg.price,
                        isPro
                      );
                    } catch (error) {
                      console.error('Error creating checkout:', error);
                      setCheckoutLoading(false);
                    }
                  }}
                >
                  {pkg.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgb(147, 51, 234)',
                        color: 'white',
                        fontSize: '10px',
                        px: 1,
                        py: 0.2,
                        borderRadius: '6px',
                        fontWeight: 'bold',
                      }}
                    >
                      {t("POPULAR")}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                    <Coins size={16} style={{ color: '#fbbf24' }} />
                    <Typography sx={{ color: 'white', fontWeight: 'bold', ml: 0.5 }}>
                      {pkg.amount}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: 'bold' }}>
                    ${pkg.price}
                  </Typography>
                </Box>
              </Grid2>
            ))}
          </Grid2>

          {/* Main CTA Button */}
          <Button
            fullWidth
            variant="contained"
            disabled={checkoutLoading}
            onClick={async () => {
              if (checkoutLoading) return;
              try {
                setCheckoutLoading(true);

                // Default to the popular package (middle one - 100 coins for $3.99)
                const popularPackage = { amount: 100, price: 3.99 };

                // Track initiate checkout event for Facebook Pixel with dynamic price
                trackInitiateStarCoinPurchase(`package_${popularPackage.amount}`, popularPackage.price);

                await starCoinsClient.createCheckout(
                  `package_${popularPackage.amount}`,
                  popularPackage.amount,
                  popularPackage.price,
                  isPro
                );
              } catch (error) {
                console.error('Error creating checkout:', error);
                setCheckoutLoading(false);
              }
            }}
            sx={{
              backgroundColor: 'rgb(147, 51, 234)',
              color: 'white',
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '16px',
              borderRadius: '12px',
              textTransform: 'none',
              opacity: checkoutLoading ? 0.6 : 1,
              '&:hover': {
                backgroundColor: 'rgb(126, 34, 206)',
              },
              '&:disabled': {
                backgroundColor: 'rgb(147, 51, 234)',
                color: 'white',
              },
            }}
          >
            {checkoutLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              t('Buy More to Keep Using')
            )}
          </Button>

          {/* Info Text */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              mt: 2,
            }}
          >
            {t("xPicks costs")} {XPICKS_COST}
            <Coins size={12} style={{ color: '#fbbf24' }} />
            {t("per use after free spins")}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MultiplasButton;